import { useCallback } from "react";

import { UseQueryResult, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import {
  FieldSatelliteImage,
  FieldSatelliteObservations,
  FieldSatelliteThumbnails,
  SatelliteDateRange,
  SatelliteImageLayer,
  getFieldSatelliteImage,
  getFieldSatelliteObservations,
  getFieldSatelliteThumbnails,
} from "services";

import { ArrayUtils, shouldRetryQueryError } from "utils";

import { GET_FIELD_SATELLITE_IMAGE, GET_FIELD_SATELLITE_OBSERVATIONS, GET_FIELD_SATELLITE_THUMBNAILS } from "consts";

// Image links are presigned for an hour, so cached answers are refetched well before they expire.
const SATELLITE_STALE_TIME = 1000 * 60 * 30;

// One retry, so a moment of "busy" recovers. None for answers that won't change, like a day without an image.
const retryOnce = (failureCount: number, error: Error) => failureCount < 1 && shouldRetryQueryError()(failureCount, error);

/** The remote sensing service had no picture of the field that day (404), as opposed to failing to make one. */
export const isNoSatelliteImageError = (error: unknown) => isAxiosError(error) && error.status === 404;

type FieldRanges = { farmId: string; fieldId: string; ranges: SatelliteDateRange[] };

// The ranges are contiguous and in order, so the loaded ones join into one span.
const mergeObservations = (results: UseQueryResult<FieldSatelliteObservations>[]) => {
  const loaded = results.flatMap(({ data }) => (data ? [data] : []));
  const [first, last] = [loaded[0], loaded[loaded.length - 1]];
  const observations = first && last ? { range: { from: first.range.from, to: last.range.to }, passes: loaded.flatMap(({ passes }) => passes) } : undefined;
  return { observations, results };
};

const groupThumbnails = (results: UseQueryResult<FieldSatelliteThumbnails>[]) => ({
  thumbnailsByMonth: ArrayUtils.groupBy(
    results.flatMap(({ data }) => data?.thumbnails ?? []),
    ({ date }) => date.slice(0, 7)
  ),
  isLoading: results.some(({ isLoading }) => isLoading),
  failed: results.filter(({ isError }) => isError),
});

/**
 * One query per date range, so a year added to the drawer is fetched (and cached) on its own. The combined result keeps
 * its reference until the data changes.
 */
export const useGetFieldSatelliteObservations = ({ farmId, fieldId, ranges }: FieldRanges) =>
  useQueries({
    queries: ranges.map((range) => ({
      queryKey: [GET_FIELD_SATELLITE_OBSERVATIONS, farmId, fieldId, range.from, range.to],
      queryFn: (): Promise<FieldSatelliteObservations> => getFieldSatelliteObservations({ farmId, fieldId, range }),
      enabled: !!farmId && !!fieldId,
      staleTime: SATELLITE_STALE_TIME,
      retry: retryOnce,
    })),
    combine: mergeObservations,
  });

/** A range's thumbnails are asked once its observations are in: both read the same days, and asking at once would compute them twice. */
export const useGetFieldSatelliteThumbnails = ({ farmId, fieldId, ranges, layer, isReady }: FieldRanges & { layer: SatelliteImageLayer; isReady: boolean[] }) =>
  useQueries({
    queries: ranges.map((range, index) => ({
      queryKey: [GET_FIELD_SATELLITE_THUMBNAILS, farmId, fieldId, range.from, range.to, layer],
      queryFn: (): Promise<FieldSatelliteThumbnails> => getFieldSatelliteThumbnails({ farmId, fieldId, range, layer }),
      enabled: !!isReady[index] && !!farmId && !!fieldId,
      staleTime: SATELLITE_STALE_TIME,
      retry: retryOnce,
    })),
    combine: groupThumbnails,
  });

type FieldDayImage = { farmId: string; fieldId: string; date: string; layer: SatelliteImageLayer };

// One definition for the hook and the prefetch, so both share the cache entry.
const satelliteImageQuery = ({ farmId, fieldId, date, layer }: FieldDayImage) => ({
  queryKey: [GET_FIELD_SATELLITE_IMAGE, farmId, fieldId, date, layer],
  queryFn: () => getFieldSatelliteImage({ farmId, fieldId, date, layer }),
  staleTime: SATELLITE_STALE_TIME,
  retry: retryOnce,
});

export const useGetFieldSatelliteImage = ({
  farmId,
  fieldId,
  date,
  layer,
  enabled = true,
}: Omit<FieldDayImage, "date"> & { date: string | null; enabled?: boolean }) =>
  useQuery<FieldSatelliteImage>({
    ...satelliteImageQuery({ farmId, fieldId, date: date ?? "", layer }),
    enabled: enabled && !!farmId && !!fieldId && !!date,
  });

const warmBrowserCache = ({ url }: FieldSatelliteImage) => {
  new window.Image().src = url;
};

/**
 * Loads a day's natural colour and NDVI images ahead of time, one after the other: the first renders both layers on the
 * remote sensing service, so the second is read from storage. Best effort, a failure only means no head start.
 */
export const usePrefetchFieldSatelliteDay = () => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ farmId, fieldId, date }: Omit<FieldDayImage, "layer">) =>
      queryClient
        .fetchQuery(satelliteImageQuery({ farmId, fieldId, date, layer: "true-color" }))
        .then(warmBrowserCache)
        .then(() => queryClient.fetchQuery(satelliteImageQuery({ farmId, fieldId, date, layer: "ndvi" })))
        .then(warmBrowserCache)
        .catch(() => undefined),
    [queryClient]
  );
};
